import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import PoolSelector from '../components/PoolSelector';
import NoPoolYet from '../components/NoPoolYet';
import UserAvatar from '../components/UserAvatar';
import Podium from '../components/Podium';

const s = {
  page: { padding: '24px 20px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 28 },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 8 },
  title: { fontFamily: "'Oswald', sans-serif", fontSize: 28, fontWeight: 700, color: '#fff' },
  subtitle: { color: '#667', fontSize: 14 },
  seasonSelector: {
    display: 'flex',
    gap: 8,
    alignItems: 'center'
  },
  seasonLabel: { fontSize: 13, color: '#889', fontWeight: 600 },
  seasonBtn: {
    padding: '6px 16px',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'transparent',
    color: '#889',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    transition: 'all 0.15s'
  },
  seasonBtnActive: {
    background: '#013369',
    border: '1px solid #013369',
    color: '#fff'
  },
  summaryCards: { display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' },
  card: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '14px 20px',
    flex: '1 1 140px'
  },
  cardValue: { fontFamily: "'Oswald', sans-serif", fontSize: 28, fontWeight: 700, color: '#7fb3ff' },
  cardLabel: { fontSize: 12, color: '#667', marginTop: 2 },
  // Summary table
  table: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 32
  },
  tableTitle: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 16,
    fontWeight: 700,
    color: '#7fb3ff',
    padding: '16px 20px 8px',
    letterSpacing: 1
  },
  thead: {
    display: 'grid',
    padding: '10px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)'
  },
  th: { fontSize: 11, fontWeight: 700, color: '#667', letterSpacing: 1, textTransform: 'uppercase' },
  trow: {
    display: 'grid',
    padding: '13px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center'
  },
  trowTop: { background: 'linear-gradient(90deg, rgba(213,10,10,0.1), transparent)', borderLeft: '3px solid #D50A0A' },
  trowMe: { background: 'linear-gradient(90deg, rgba(1,51,105,0.2), transparent)', borderLeft: '3px solid #013369' },
  rank: { fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, color: '#445' },
  // Weekly grid
  gridTable: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    overflow: 'auto',
    marginBottom: 32
  },
  gridInner: { minWidth: 600 },
  gridHead: {
    display: 'flex',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)',
    padding: '10px 20px'
  },
  gridHeadName: { width: 150, flexShrink: 0, fontSize: 11, fontWeight: 700, color: '#667', letterSpacing: 1, textTransform: 'uppercase' },
  gridHeadWeek: { flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#667', letterSpacing: 0.5, minWidth: 60 },
  gridHeadTotal: { width: 80, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#7fb3ff', letterSpacing: 1 },
  gridRow: {
    display: 'flex',
    padding: '10px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center'
  },
  gridRowMe: { background: 'rgba(1,51,105,0.1)' },
  gridName: { width: 150, flexShrink: 0, fontSize: 14, fontWeight: 600, color: '#dde' },
  gridCell: { flex: 1, textAlign: 'center', minWidth: 60 },
  gridTotal: { width: 80, textAlign: 'center' },
  cellCorrect: { fontSize: 13, fontWeight: 700, color: '#4caf50' },
  cellWrong: { fontSize: 11, color: '#f44336', marginTop: 1 },
  cellEmpty: { fontSize: 18, color: '#334' },
  winPct: { fontSize: 14, fontWeight: 700 },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#445' },
  emptyEmoji: { fontSize: 52, display: 'block', marginBottom: 16 },
  emptyTitle: { fontFamily: "'Oswald', sans-serif", fontSize: 22, color: '#667', marginBottom: 8 }
};

function getRankEmoji(rank) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return rank;
}

function getWinPctColor(pct) {
  if (pct >= 70) return '#4caf50';
  if (pct >= 55) return '#8bc34a';
  if (pct >= 45) return '#ffa726';
  return '#f44336';
}

export default function SeasonStandingsPage({ user, pools, poolsLoaded, selectedPool, onSelectPool, onManagePools }) {
  const [data, setData] = useState(null);
  /* Which of the three records is on screen. 2 is the regular season, which is
     what "the record" means; preseason and playoffs are their own competitions
     and get their own tabs rather than being folded in or hidden. */
  const [phase, setPhase] = useState(2);
  const [loading, setLoading] = useState(true);
  const prevPoolIdRef = useRef(undefined);
  const prevSeasonRef = useRef(undefined);

  const reqRef = useRef(0);

  const load = useCallback(async (season, poolId, seasonType) => {
    const ticket = ++reqRef.current;
    // Only show loading spinner when pool or season actually changes (not on background refreshes)
    const isFirstLoad = prevPoolIdRef.current === undefined;
    const poolChanged = poolId !== prevPoolIdRef.current;
    const seasonChanged = season !== prevSeasonRef.current;
    if (isFirstLoad || poolChanged || seasonChanged) {
      setLoading(true);
    }
    prevPoolIdRef.current = poolId;
    prevSeasonRef.current = season;
    try {
      const params = [];
      if (season) params.push(`season=${season}`);
      if (poolId) params.push(`pool_id=${poolId}`);
      if (seasonType) params.push(`season_type=${seasonType}`);
      const url = '/picks/season-standings' + (params.length ? '?' + params.join('&') : '');
      const res = await API.get(url);
      if (ticket !== reqRef.current) return;   // a newer pool/season won the race
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      if (ticket === reqRef.current) setLoading(false);
    }
  }, []);

  // Only re-run when the pool ID value changes, not when the pool object reference changes
  const poolId = selectedPool?.id;
  useEffect(() => { load(undefined, poolId, phase); }, [load, poolId, phase]);

  // Same as the weekly board: no pool, nothing to be ranked in. Shown ahead of
  // the spinner so a stranger board never flashes up first.
  if (poolsLoaded && (!pools || pools.length === 0)) return <NoPoolYet onGoToPools={onManagePools} board="season" />;

  if (loading) return (
    <div style={{ ...s.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>📊</div>
      <div style={{ color: '#667' }}>Loading standings...</div>
    </div>
  );

  if (!data || data.standings.length === 0) return (
    <div style={s.page}>
      <div style={s.emptyState}>
        <span style={s.emptyEmoji}>📊</span>
        <div style={s.emptyTitle}>No Season Data Yet</div>
        <p style={{ color: '#445', fontSize: 14 }}>Season standings will appear once picks are submitted and games are completed.</p>
      </div>
    </div>
  );

  const { standings, weeks, seasons, targetSeason, availablePhases } = data;
  /* Only phases with games get a tab, so Playoffs appears in January and not
     before, and a brand new season does not offer three empty boards. */
  const tabs = (availablePhases || []).filter(ph => ph.weeks > 0);
  const activeTab = tabs.find(t => t.type === phase);
  const myEntry = standings.find(entry => entry.username === user.username);
  const totalWeeksPlayed = weeks.filter(w =>
    standings.some(st => st.weeklyResults[w.id]?.submitted)
  ).length;

  // Six cells per row — rank, name, W, L, pending, pct — so six tracks. Five
  // tracks wrapped the percentage onto an implicit second row.
  //
  // minmax(0,...) rather than a bare 1fr: an email-address username has no
  // break opportunity, so a bare 1fr track grows to fit it and pushes W, L and
  // the percentage out of a card that hides its overflow. The record is what
  // this table is for, so the name gives way instead.
  const summaryGrid = 'var(--ss-cols, 44px minmax(0,1fr) 54px 54px 44px 56px)';

  /* Preseason reads as the smaller thing it is: present, selectable, and not
     dressed up like the record that counts. */
  const tabStyle = (t) => ({
    padding: '7px 14px',
    borderRadius: 8,
    border: '1px solid ' + (t.type === phase ? 'rgba(127,179,255,0.5)' : 'rgba(255,255,255,0.10)'),
    background: t.type === phase ? 'rgba(127,179,255,0.15)' : 'rgba(255,255,255,0.03)',
    color: t.type === phase ? '#7fb3ff' : (t.type === 1 ? '#778' : '#99a'),
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: 0.3,
    minHeight: 40
  });

  return (
    <div>
      <PoolSelector pools={pools} selectedPool={selectedPool} onSelect={onSelectPool} onManage={onManagePools} />
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.topRow}>
          <div>
            <div style={s.title}>📊 Season Standings</div>
            <div style={s.subtitle}>
              {activeTab
                ? `Cumulative ${activeTab.label.toLowerCase()} record`
                : 'Cumulative record across all weeks'}
            </div>
          </div>
          {seasons.length > 1 && (
            <div style={s.seasonSelector}>
              <span style={s.seasonLabel}>Season:</span>
              {seasons.map(yr => (
                <button
                  key={yr}
                  style={{ ...s.seasonBtn, ...(yr === targetSeason ? s.seasonBtnActive : {}) }}
                  onClick={() => load(yr, selectedPool?.id, phase)}
                >{yr}</button>
              ))}
            </div>
          )}
        </div>

        {/* Three records, not one. Preseason is the testing ground and the
            playoffs are their own short run; each totals separately so none of
            them dilutes the others. Only phases with games get a tab. */}
        {tabs.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {tabs.map(t => (
              <button
                key={t.type}
                style={tabStyle(t)}
                onClick={() => setPhase(t.type)}
                aria-pressed={t.type === phase}
              >
                {t.label}
                <span style={{ color: '#556', fontWeight: 600, marginLeft: 6, fontSize: 12 }}>
                  {t.weeks}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={s.summaryCards}>
        <div style={s.card}>
          <div style={s.cardValue}>{standings.length}</div>
          <div style={s.cardLabel}>Players</div>
        </div>
        <div style={s.card}>
          <div style={s.cardValue}>{totalWeeksPlayed}</div>
          <div style={s.cardLabel}>Weeks played</div>
        </div>
        {myEntry && (
          <>
            <div style={s.card}>
              <div style={{ ...s.cardValue, color: '#4caf50' }}>{myEntry.totalCorrect}</div>
              <div style={s.cardLabel}>Your total correct</div>
            </div>
            <div style={s.card}>
              <div style={{ ...s.cardValue, color: getWinPctColor(myEntry.winPct) }}>{myEntry.winPct}%</div>
              <div style={s.cardLabel}>Your win %</div>
            </div>
            <div style={s.card}>
              <div style={{ ...s.cardValue, color: '#D50A0A' }}>#{myEntry.rank}</div>
              <div style={s.cardLabel}>Your rank</div>
            </div>
          </>
        )}
      </div>

      {/* Top three on the season. Same podium as the weekly board. */}
      <Podium unit="correct"
        rows={standings.slice(0, 3).map(e => ({ username: e.username, rank: e.rank, score: e.totalCorrect }))} />

      {/* Season totals table */}
      <style>{`
        .ss-table { --ss-cols: 44px minmax(0,1fr) 54px 54px 44px 56px; }
        .ss-rank-short { display: none; }
        @media (max-width: 640px) {
          /* Six tracks plus 40px of side padding left the name about 30px on a
             phone. Pending is the one column nobody opens this screen for. */
          .ss-table { --ss-cols: 30px minmax(0,1fr) 40px 40px 46px; }
          .ss-table .ss-col-pending { display: none; }
          .ss-table > div { padding-left: 12px; padding-right: 12px; }
          /* "RANK" is wider than the 30px rank track and ran into PLAYER. */
          .ss-rank-long { display: none; }
          .ss-rank-short { display: inline; }
        }
      `}</style>
      <div style={s.table} className="ss-table">
        <div style={s.tableTitle}>SEASON TOTALS {targetSeason}</div>
        <div style={{ ...s.thead, gridTemplateColumns: summaryGrid }}>
          <div style={s.th}>
            <span className="ss-rank-long">Rank</span>
            <span className="ss-rank-short">#</span>
          </div>
          <div style={s.th}>Player</div>
          <div style={{ ...s.th, textAlign: 'center' }}>✅ W</div>
          <div style={{ ...s.th, textAlign: 'center' }}>❌ L</div>
            <div style={{ ...s.th, textAlign: 'center' }} className="ss-col-pending">⏳</div>
            <div style={{ ...s.th, textAlign: 'center' }}>%</div>
          </div>

          {standings.map(function(entry) {
            const isMe = entry.user_id != null ? entry.user_id === user.id : entry.username === user.username;
            const isTop = entry.rank === 1;
            // The API sends totalCorrect/totalWrong/winPct — reading correct/
            // wrong/total left every W-L cell blank and every percentage at 0%.
            const pct = entry.winPct ?? 0;
            return (
              <div key={entry.entry_id ?? entry.user_id} style={{ ...s.trow, gridTemplateColumns: summaryGrid, ...(isTop ? s.trowTop : {}), ...(isMe ? s.trowMe : {}) }}>
                <div style={{ ...s.rank, color: isTop ? '#FFD700' : entry.rank === 2 ? '#C0C0C0' : entry.rank === 3 ? '#CD7F32' : '#445' }}>
                  {entry.rank}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                  <UserAvatar username={entry.username} size={28} />
                  <span title={entry.username}
                    style={{ fontSize:14, fontWeight:600, color: isMe ? '#7fb3ff' : '#dde', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {entry.username}
                  </span>
                  {isMe && <span style={{ fontSize:11, color:'#667', flexShrink:0 }}>(you)</span>}
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:'#4caf50', textAlign:'center' }}>{entry.totalCorrect}</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#f44336', textAlign:'center' }}>{entry.totalWrong}</div>
                <div style={{ fontSize:13, color:'#889', textAlign:'center' }} className="ss-col-pending">{entry.totalPending > 0 ? entry.totalPending : '-'}</div>
                <div style={{ fontSize:13, fontWeight:700, textAlign:'center', color: pct >= 60 ? '#4caf50' : pct >= 45 ? '#ffa726' : '#ef5350' }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ height:40 }} />
    </div>
  );
}
